import { Signal } from '@angular/core';
import { WebhookEndpoint } from '@tourfold/libraries/tf-api-client-public-v2';
import { providePageContext } from '@tourfold/shared-kernels/page-context';

const MAX_VISIBLE_WEBHOOKS = 25;

// Page-context only reads `id` and `displayName`; both live on the base
// `WebhookEndpoint`, so we keep the helper unaware of the component's
// extended `WebhookEndpointWithSubscriptions` type (which is local + private
// to webhooks-list.ts today).
export interface WebhooksAiPageContextSignals {
  readonly endpoints: Signal<readonly WebhookEndpoint[]>;
  readonly editingEndpoint: Signal<WebhookEndpoint | null>;
  readonly testingEndpointId: Signal<string | null>;
}

/**
 * Registers AI assistant page context for the webhooks settings list.
 *
 * - `entity` is the user's current focus: the endpoint being edited
 *   (dialog open) wins; otherwise the endpoint currently under test;
 *   otherwise undefined.
 * - `visible_rows` carries the first 25 endpoints with their display
 *   names so the model can refer to them by name.
 *
 * No `filters` — the list is not data-narrowed in this view.
 *
 * See frontend/AGENTS.md → "AI Assistant Page Context" for the field rules.
 */
export function provideWebhooksAiPageContext(state: WebhooksAiPageContextSignals): void {
  providePageContext(() => {
    const editing = state.editingEndpoint();
    const testingId = state.testingEndpointId();
    const endpoints = state.endpoints();

    const focused: WebhookEndpoint | null =
      editing ??
      (testingId ? (endpoints.find((endpoint) => endpoint.id === testingId) ?? null) : null);

    return {
      entity: focused
        ? {
            type: 'webhook',
            id: focused.id,
            label: focused.displayName,
          }
        : undefined,
      visible_rows: endpoints.slice(0, MAX_VISIBLE_WEBHOOKS).map((endpoint) => ({
        type: 'webhook',
        id: endpoint.id,
        label: endpoint.displayName,
      })),
    };
  });
}
